import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query';
import { getCachedTranslations, storeCachedTranslation } from '@utils/translations';

const localeApi = createApi({
    reducerPath: 'localeApi',
    baseQuery: fetchBaseQuery({
        baseUrl: '/static/build/lang',
        credentials: 'same-origin',
        validateStatus: (response, _result) => {
            return (response.status >= 200 && response.status <= 299) || response.status === 304;
        },
        responseHandler: async (response) => {
            if (response.status === 304) {
                const url = response.url;
                const locale = url?.split('/').pop()?.replace('.json', '') || '';
                const cachedTranslations = getCachedTranslations();
                const cachedData = cachedTranslations[locale];

                if (cachedData?.translations) {
                    return cachedData.translations;
                } else {
                    console.warn(`304 response but no cached translations found for locale: ${locale}`);
                    throw new Error(`No cached translations for ${locale}`);
                }
            }

            return await response.json();
        },
        prepareHeaders: (headers, { endpoint, arg }) => {
            if (endpoint === 'getTranslations') {
                const locale = arg as string;
                const cachedTranslations = getCachedTranslations();
                const cachedData = cachedTranslations[locale];

                if (cachedData?.etag && cachedData?.translations) {
                    headers.set('If-None-Match', cachedData.etag);
                }
            }
            return headers;
        },
    }),
    // Keep data cached for 30 minutes, in case user switches back to a recently used language
    keepUnusedDataFor: 1800,
    endpoints: (builder) => ({
        getTranslations: builder.query<Record<string, string> | null, string>({
            query: (locale: string) => `${locale}.json`,
            transformResponse: (response: Record<string, string>, meta, arg) => {
                try {
                    const etag = meta?.response?.headers?.get('etag');
                    let status = meta?.response?.status;
                    const locale = arg;

                    if (status === 304) {
                        console.warn('304 reached transformResponse (should have been handled by responseHandler!)');
                        const cachedTranslations = getCachedTranslations();
                        const cachedData = cachedTranslations[locale];

                        if (!cachedData?.translations) {
                            console.warn(`No cached translations found for locale: ${locale}, despite 304 response`);
                            return null;
                        }

                        return cachedData.translations;
                    } else if (status === 200) {
                        if (!response || typeof response !== 'object') {
                            const errorMsg = `Invalid translation response format for locale: ${locale}`;
                            console.error(errorMsg, response);
                            throw new Error(errorMsg);
                        }

                        if (etag) {
                            try {
                                storeCachedTranslation(locale, etag, response);
                            } catch (cacheError) {
                                console.warn(`Failed to cache translations for locale: ${locale}`, cacheError);
                            }
                        } else {
                            console.warn(`No etag received for locale: ${locale}, skipping cache`);
                        }
                    }

                    return response;
                } catch (error) {
                    console.error('Error while transforming translation response:', error);
                    throw error;
                }
            },
        }),
    }),
});

export default localeApi;